// File Name: lib/ynoMatchAuth.js
//
// The one place the website signs anyone in to the YNO **app's** Firebase
// project. Kept out of `ynoMatchDb.js` on purpose — that module is read-only by
// contract and its header says so.
//
// Used by /join: when the Worker creates an account for a visitor it hands back
// a Firebase custom token, and this exchanges it for a session so the person is
// signed in on the site without ever seeing a login form.
//
// Three things worth knowing:
//
//  * It signs into the NAMED `yno-match` app, never the default one. The default
//    app is this website's own project (`yalla-nellab-12650`) and signing a
//    visitor into it would be a different account entirely.
//  * Everything is dynamically imported. `firebase/auth` is a meaningful chunk
//    of bundle that only matters after someone actually joins, and keeping it
//    out of module scope means the join page still renders if it fails to load.
//  * It NEVER throws and never blocks the join. The join has already been
//    written by the server by the time this runs; a failed sign-in only costs
//    the visitor a session they did not ask for.

/**
 * Exchange a Firebase custom token for a signed-in session.
 *
 * @param {string} customToken from the Worker's /guest-join response. Only ever
 *   present for an account that request just created — the server will not mint
 *   one for an account that already existed, because /guest-join is public and
 *   accepts any email address.
 * @returns {Promise<boolean>} whether the sign-in actually happened.
 */
export async function signInSilently(customToken) {
  if (typeof customToken !== 'string' || customToken.length < 20) return false;
  try {
    const [{ ynoApp }, { getAuth, signInWithCustomToken }] = await Promise.all([
      import('./ynoMatchDb'),
      import('firebase/auth'),
    ]);
    if (!ynoApp) return false;
    await signInWithCustomToken(getAuth(ynoApp), customToken);
    return true;
  } catch (err) {
    // Deliberately swallowed. Being signed in is a convenience here, not a
    // requirement: the player is already in the match either way.
    return false;
  }
}
